import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, X, Loader2, Key, Trash2, UserCheck, UserX,
  Shield, ShoppingCart, Package, Truck, Edit2, RefreshCw, Copy
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';

const ROLES = [
  { value: 'manager', icon: Shield, color: 'bg-blue-100 text-blue-700' },
  { value: 'cashier', icon: ShoppingCart, color: 'bg-green-100 text-green-700' },
  { value: 'stock_clerk', icon: Package, color: 'bg-amber-100 text-amber-700' },
  { value: 'driver', icon: Truck, color: 'bg-purple-100 text-purple-700' },
];

const StaffPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [staff, setStaff] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedPin, setCopiedPin] = useState(null);

  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'cashier' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const shopsRes = await api.get('/shops');
      if (shopsRes.data.length === 0) { navigate('/shop/setup'); return; }
      const s = shopsRes.data[0];
      setShop(s);
      const staffRes = await api.get(`/shops/${s.id}/staff`);
      setStaff(staffRes.data);
    } catch (e) { console.error('Failed to load staff:', e); }
    finally { setLoading(false); }
  };

  const resetForm = () => setForm({ name: '', email: '', phone: '', role: 'cashier' });

  const handleCreate = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      // Don't send empty strings for optional fields
      const payload = {
        name: form.name,
        role: form.role,
        ...(form.email && { email: form.email }),
        ...(form.phone && { phone: form.phone }),
      };
      await api.post(`/shops/${shop.id}/staff`, payload);
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error creating staff'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editStaff) return;
    setSaving(true);
    try {
      // Don't send empty strings for optional fields
      const payload = {
        name: form.name,
        role: form.role,
        ...(form.email && { email: form.email }),
        ...(form.phone && { phone: form.phone }),
      };
      await api.put(`/shops/${shop.id}/staff/${editStaff.id}`, payload);
      setEditStaff(null);
      resetForm();
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error updating staff'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (member) => {
    try {
      await api.put(`/shops/${shop.id}/staff/${member.id}`, { is_active: !member.is_active });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (memberId) => {
    if (!confirm(t('staff_delete_confirm') || 'Delete this staff member?')) return;
    try {
      await api.delete(`/shops/${shop.id}/staff/${memberId}`);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleRegenPin = async (memberId) => {
    try {
      const res = await api.post(`/shops/${shop.id}/staff/${memberId}/regenerate-pin`);
      alert(`${t('staff_new_pin') || 'New PIN'}: ${res.data.new_pin}`);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const copyPin = (pin) => {
    navigator.clipboard.writeText(pin);
    setCopiedPin(pin);
    setTimeout(() => setCopiedPin(null), 2000);
  };

  const openEdit = (member) => {
    setForm({ name: member.name, email: member.email || '', phone: member.phone || '', role: member.role });
    setEditStaff(member);
  };

  const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[1];
  const roleLabel = (role) => t(`staff_role_${role}`) || role;

  const active = staff.filter(s => s.is_active);
  const inactive = staff.filter(s => !s.is_active);

  if (loading) {
    return <ShopLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>;
  }

  return (
    <ShopLayout>
      <div className="space-y-6" data-testid="staff-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('staff_title') || 'Staff Management'}</h2>
            <p className="text-slate-500 text-sm">{staff.length} {t('staff_members') || 'team members'}</p>
          </div>
          <button onClick={() => { resetForm(); setShowCreate(true); }}
            className="vf-btn-primary flex items-center gap-2" data-testid="add-staff-btn">
            <Plus className="w-5 h-5" /> {t('staff_add') || 'Add Staff'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {ROLES.map(r => {
            const count = staff.filter(s => s.role === r.value && s.is_active).length;
            return (
              <div key={r.value} className="bg-white border-2 border-slate-100 p-4" data-testid={`staff-stat-${r.value}`}>
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${r.color} mb-2`}>
                  <r.icon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{count}</p>
                <p className="text-sm text-slate-500 capitalize">{roleLabel(r.value)}</p>
              </div>
            );
          })}
        </div>

        {/* Active Staff */}
        {active.length > 0 && (
          <div className="bg-white border-2 border-slate-100 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-semibold text-[#1e3a5f] flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#22c55e]" /> {t('staff_active') || 'Active Staff'} ({active.length})
              </h3>
            </div>
            <div className="divide-y divide-slate-50">
              {active.map(member => {
                const roleInfo = getRoleInfo(member.role);
                return (
                  <div key={member.id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`staff-row-${member.id}`}>
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${roleInfo.color}`}>
                      <roleInfo.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1e3a5f]">{member.name}</p>
                      <p className="text-xs text-slate-400">{roleLabel(member.role)}{member.email ? ` · ${member.email}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="bg-[#1e3a5f]/5 px-3 py-1.5 rounded-sm flex items-center gap-2" data-testid={`pin-display-${member.id}`}>
                        <Key className="w-4 h-4 text-[#1e3a5f]" />
                        <span className="font-mono font-bold text-[#1e3a5f] tracking-wider">{member.pin}</span>
                        <button onClick={() => copyPin(member.pin)} className="hover:text-[#22c55e]" title="Copy">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        {copiedPin === member.pin && <span className="text-[10px] text-[#22c55e]">OK</span>}
                      </div>
                      <button onClick={() => handleRegenPin(member.id)}
                        className="p-2 hover:bg-amber-50 rounded-sm text-amber-600" title={t('staff_regen_pin') || 'New PIN'}
                        data-testid={`regen-pin-${member.id}`}>
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button onClick={() => openEdit(member)}
                        className="p-2 hover:bg-blue-50 rounded-sm text-blue-600" title={t('staff_edit') || 'Edit'}
                        data-testid={`edit-staff-${member.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggleActive(member)}
                        className="p-2 hover:bg-slate-100 rounded-sm text-slate-400" title={t('staff_deactivate') || 'Deactivate'}
                        data-testid={`deactivate-${member.id}`}>
                        <UserX className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Inactive Staff */}
        {inactive.length > 0 && (
          <div className="bg-white border-2 border-slate-100 overflow-hidden opacity-70">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-500 flex items-center gap-2">
                <UserX className="w-5 h-5" /> {t('staff_inactive') || 'Inactive Staff'} ({inactive.length})
              </h3>
            </div>
            <div className="divide-y divide-slate-50">
              {inactive.map(member => (
                <div key={member.id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`staff-row-${member.id}`}>
                  <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-slate-100 text-slate-400">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-500">{member.name}</p>
                    <p className="text-xs text-slate-400">{roleLabel(member.role)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleToggleActive(member)}
                      className="p-2 hover:bg-green-50 rounded-sm text-green-600" title={t('staff_activate') || 'Activate'}
                      data-testid={`activate-${member.id}`}>
                      <UserCheck className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(member.id)}
                      className="p-2 hover:bg-red-50 rounded-sm text-red-500" title={t('staff_delete') || 'Delete'}
                      data-testid={`delete-staff-${member.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {staff.length === 0 && (
          <div className="bg-white border-2 border-slate-100 p-8 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">{t('staff_empty') || 'No Staff Yet'}</h3>
            <p className="text-sm text-slate-500 mb-4">{t('staff_empty_desc') || 'Add your first team member to get started'}</p>
            <button onClick={() => { resetForm(); setShowCreate(true); }}
              className="vf-btn-primary inline-flex items-center gap-2">
              <Plus className="w-5 h-5" /> {t('staff_add') || 'Add Staff'}
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {(showCreate || editStaff) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50" onClick={() => { setShowCreate(false); setEditStaff(null); }} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-[95vw] md:w-[440px] max-h-[90vh] overflow-y-auto bg-white z-10 p-6 rounded-lg shadow-xl"
              data-testid="staff-modal">
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-4">
                {editStaff ? (t('staff_edit') || 'Edit Staff') : (t('staff_add') || 'Add Staff')}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('staff_name') || 'Name'} *</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    data-testid="staff-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('staff_role_label') || 'Role'}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ROLES.map(r => (
                      <button key={r.value} onClick={() => setForm(f => ({ ...f, role: r.value }))}
                        className={`flex items-center gap-2 p-3 border-2 rounded-sm transition-colors ${
                          form.role === r.value ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' : 'border-slate-200 hover:border-slate-300'
                        }`} data-testid={`role-btn-${r.value}`}>
                        <r.icon className="w-5 h-5 text-[#1e3a5f]" />
                        <span className="text-sm font-medium text-[#1e3a5f] capitalize">{roleLabel(r.value)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Email</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_setup_phone') || 'Phone'}</label>
                    <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => { setShowCreate(false); setEditStaff(null); resetForm(); }}
                  className="vf-btn-secondary flex-1">{t('products_cancel') || 'Cancel'}</button>
                <button onClick={editStaff ? handleUpdate : handleCreate}
                  disabled={saving || !form.name}
                  className="vf-btn-primary flex-1 disabled:opacity-50" data-testid="save-staff-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> :
                    editStaff ? (t('staff_save') || 'Save') : (t('staff_create') || 'Create')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </ShopLayout>
  );
};

export default StaffPage;
